import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRunbookScoreboard, getRunbookTrends, wavesSuggest, wavesApply } from "../lib/api";
import { toast } from "sonner";
import { PrintButton } from "../components/PrintButton";
import { useAuth } from "../context/AuthContext";
import { LayoutGrid, ChevronRight, CheckCircle2, FileDown, TrendingUp, Wand2, X, Sparkles, ShieldCheck } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const WAVE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#a855f7", "#ef4444", "#06b6d4", "#ec4899"];

const MIG = {
  pending: { label: "Pending", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  onboarded: { label: "Onboarded", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  cutover: { label: "Cutover", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
};
const E911 = {
  compliant: { label: "Compliant", cls: "text-emerald-400" },
  review: { label: "Review", cls: "text-amber-400" },
  pending: { label: "Pending", cls: "text-blue-400" },
  "non-compliant": { label: "Non-Compliant", cls: "text-red-400" },
};
const DEV = {
  empty: { cls: "bg-zinc-700/20 text-zinc-500 border-zinc-700" },
  ok: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25" },
  warning: { cls: "bg-amber-500/10 text-amber-400 border-amber-500/25" },
  blocker: { cls: "bg-red-500/10 text-red-400 border-red-500/25" },
};

const pctCls = (p) => p === null ? "text-zinc-600" : p >= 80 ? "text-emerald-400" : p >= 40 ? "text-amber-400" : "text-red-400";
const barCls = (p) => p >= 80 ? "bg-emerald-500" : p >= 40 ? "bg-amber-500" : "bg-blue-500";

export default function Scoreboard() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [track, setTrack] = useState("webex");
  const [suggestion, setSuggestion] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [applying, setApplying] = useState(false);
  const navigate = useNavigate();
  const { isPro } = useAuth();

  const openReadiness = () => {
    if (!isPro) {
      toast.error("Cutover readiness reports are a Pro feature — upgrade to export.", {
        action: { label: "Upgrade", onClick: () => navigate("/billing") },
      });
      return;
    }
    navigate(`/readiness/report?track=${track}`);
  };

  const load = () => {
    getRunbookScoreboard(track).then(setData).catch(() => {});
    getRunbookTrends().then(setTrends).catch(() => {});
  };
  useEffect(() => { setData(null); load(); /* eslint-disable-next-line */ }, [track]);

  const isWebex = track === "webex";
  const TRACKS = [
    { id: "webex", label: "Webex Migration" },
    { id: "cucm15", label: "Upgrade to CUCM 15" },
  ];

  const suggest = async () => {
    setSuggesting(true);
    try { setSuggestion(await wavesSuggest()); }
    catch { toast.error("Suggest failed"); }
    setSuggesting(false);
  };
  const applySuggestion = async () => {
    if (!suggestion) return;
    setApplying(true);
    const assignments = [];
    suggestion.waves.forEach((w) => w.sites.forEach((s) => assignments.push({ site_id: s.site_id, wave: w.name })));
    try {
      const res = await wavesApply(assignments);
      toast.success(`Applied waves to ${res.applied} site(s)`);
      setSuggestion(null);
      load();
    } catch { toast.error("Apply failed"); }
    setApplying(false);
  };

  // Merge per-wave trend series into a single date-keyed dataset for the chart.
  const trendWaves = trends?.waves || [];
  const trendDates = Array.from(new Set(trendWaves.flatMap((w) => w.points.map((p) => p.date)))).sort();
  const trendData = trendDates.map((d) => {
    const row = { date: d.slice(5) };
    trendWaves.forEach((w) => { const p = w.points.find((x) => x.date === d); if (p) row[w.wave] = p.avg_pct; });
    return row;
  });

  // Group site rows by migration wave, preserving first-seen order; unassigned last.
  const groups = React.useMemo(() => {
    if (!data) return [];
    const order = [];
    const map = {};
    data.sites.forEach((s) => {
      const key = s.wave || "__none__";
      if (!map[key]) { map[key] = []; order.push(key); }
      map[key].push(s);
    });
    order.sort((a, b) => (a === "__none__" ? 1 : b === "__none__" ? -1 : 0));
    return order.map((k) => ({ wave: k === "__none__" ? "Unassigned" : k, sites: map[k] }));
  }, [data]);

  const hasWaves = data && data.sites.some((s) => s.wave);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> estate <ChevronRight className="h-3 w-3" /> scoreboard
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-7xl">
        <div className="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900 p-0.5" data-testid="scoreboard-track-switch">
            {TRACKS.map((t) => (
              <button key={t.id} onClick={() => setTrack(t.id)} data-testid={`sb-track-${t.id}`}
                className={`text-[12px] font-semibold px-3 py-1.5 rounded transition-colors ${track === t.id ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            {isWebex && (
              <button onClick={suggest} disabled={suggesting} data-testid="suggest-waves-btn"
                className="flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-md border border-blue-600/40 bg-blue-600/10 hover:bg-blue-600/20 text-blue-300 transition-colors disabled:opacity-50">
                <Wand2 className="h-4 w-4" /> {suggesting ? "Planning…" : "Auto-suggest waves"}
              </button>
            )}
            <button onClick={openReadiness} data-testid="readiness-report-btn"
              className="flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-md border border-emerald-600/40 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-300 transition-colors">
              <ShieldCheck className="h-4 w-4" /> Readiness Report
            </button>
            <button onClick={() => navigate(`/scoreboard/export?track=${track}`)} data-testid="scoreboard-export-btn"
              className="flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors">
              <FileDown className="h-4 w-4" /> Export PDF
            </button>
            <PrintButton label="Print Scoreboard" />
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <LayoutGrid className="h-4 w-4" /> Estate Scoreboard
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">
          {isWebex ? "Migration at a Glance" : "CUCM 15 Upgrade at a Glance"}
        </h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          {isWebex
            ? <>Every location's Webex migration, gate progress, compliance and readiness in one colour-coded grid{hasWaves ? ", grouped by migration wave" : ""}. Set a wave on each site in Cloud Migration.</>
            : "Every location's CUCM 15 upgrade progress and gate sign-off across the Assess → Prepare → Upgrade → Validate → Optimize lifecycle."}
        </p>

        {!data ? (
          <div className="mt-8 text-zinc-500 font-mono text-sm">Loading scoreboard…</div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-zinc-800" data-testid="scoreboard-grid">
            <table className="w-full text-left border-collapse min-w-[940px]">
              <thead>
                <tr className="bg-zinc-900/60 text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-3 font-semibold">Location</th>
                  <th className="px-4 py-3 font-semibold">Migration</th>
                  <th className="px-4 py-3 font-semibold">Phases (gate approved)</th>
                  <th className="px-4 py-3 font-semibold">Runbook</th>
                  <th className="px-4 py-3 font-semibold">E911</th>
                  <th className="px-4 py-3 font-semibold">Rooms</th>
                  <th className="px-4 py-3 font-semibold">Readiness</th>
                  <th className="px-4 py-3 font-semibold">Devices</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((grp) => {
                  const cut = grp.sites.filter((s) => s.mig_status === "cutover").length;
                  const avg = Math.round(grp.sites.reduce((a, s) => a + s.runbook_pct, 0) / grp.sites.length);
                  return (
                    <React.Fragment key={grp.wave}>
                      {hasWaves && (
                        <tr data-testid={`wave-header-${grp.wave}`} className="bg-blue-600/5 border-t border-blue-600/20">
                          <td colSpan={8} className="px-4 py-2">
                            <div className="flex items-center gap-3 text-[12px]">
                              <span className="uppercase tracking-widest font-bold text-blue-300">{grp.wave}</span>
                              <span className="text-zinc-500">{grp.sites.length} site(s) · {cut} cutover · avg runbook {avg}%</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {grp.sites.map((s) => (
                        <tr key={s.site_id} data-testid={`scoreboard-row-${s.order}`} className="border-t border-zinc-800 hover:bg-zinc-900/40">
                          <td className="px-4 py-3">
                            <div className="text-sm text-white font-medium">{s.name}</div>
                            <div className="text-[11px] text-zinc-500">{s.users != null ? `${s.users} users` : "—"} · {s.current_phase}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${MIG[s.mig_status]?.cls || ""}`}>
                              {MIG[s.mig_status]?.label || s.mig_status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1" title={`${s.gates_approved}/${s.gates_total} gates`}>
                              {s.phases.map((p) => (
                                <span
                                  key={p.id}
                                  title={`${p.name}: ${p.pct}%${p.approved ? " · gate ✓ " + (p.approver || "") : ""}`}
                                  className={`h-4 w-4 rounded-sm flex items-center justify-center text-[8px] font-bold border ${
                                    p.approved ? "bg-emerald-500/25 border-emerald-500/50 text-emerald-300"
                                    : p.pct > 0 ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
                                    : "bg-zinc-800 border-zinc-700 text-zinc-600"}`}
                                >
                                  {p.approved ? "✓" : ""}
                                </span>
                              ))}
                              <span className="ml-1.5 text-[11px] text-zinc-500 font-mono">{s.gates_approved}/{s.gates_total}</span>
                              {s.complete && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 ml-1" />}
                            </div>
                          </td>
                          <td className="px-4 py-3 w-40">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                                <div className={`h-full ${barCls(s.runbook_pct)}`} style={{ width: `${s.runbook_pct}%` }} />
                              </div>
                              <span className={`text-[12px] font-mono ${pctCls(s.runbook_pct)}`}>{s.runbook_pct}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[13px] font-semibold ${E911[s.e911_status]?.cls || "text-zinc-600"}`}>
                              {E911[s.e911_status]?.label || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[13px] text-zinc-300 font-mono">{s.rooms_count}</td>
                          <td className="px-4 py-3">
                            <span className={`font-heading text-lg font-extrabold ${pctCls(s.readiness_pct)}`}>
                              {s.readiness_pct === null ? "—" : `${s.readiness_pct}%`}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-testid={`devices-cell-${s.order}`}>
                            {(() => {
                              const d = DEV[s.device_status] || DEV.empty;
                              const blk = s.device_counts?.blocker || 0;
                              const warn = (s.device_counts?.warning || 0) + (s.device_counts?.unknown || 0);
                              return (
                                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${d.cls}`}
                                  title={s.device_models ? `${s.device_models} model(s): ${blk} blocker, ${warn} review` : "No devices added"}>
                                  {s.device_status === "empty" ? "—" : s.device_status === "blocker" ? `✕ ${blk}` : s.device_status === "warning" ? `⚠ ${warn}` : "✓"}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Trends */}
        {isWebex && (
        <div className="mt-8" data-testid="scoreboard-trends">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
            <TrendingUp className="h-4 w-4" /> Runbook Momentum by Wave
          </div>
          <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            {trendData.length < 2 ? (
              <div className="text-[13px] text-zinc-500 py-8 text-center">
                Collecting data — a daily snapshot of each wave's average runbook % is captured every time this page loads. Trends appear here across days.
                {trendData.length === 1 && (
                  <div className="mt-2 text-zinc-400">Today: {trendWaves.map((w) => `${w.wave} ${w.points[w.points.length - 1]?.avg_pct}%`).join(" · ")}</div>
                )}
              </div>
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={trendData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
                    <YAxis domain={[0, 100]} stroke="#71717a" fontSize={11} unit="%" />
                    <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {trendWaves.map((w, i) => (
                      <Line key={w.wave} type="monotone" dataKey={w.wave} stroke={WAVE_COLORS[i % WAVE_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {suggestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 no-print" data-testid="suggest-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSuggestion(null)} />
          <div className="relative w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-blue-400" />
                <span className="text-sm font-bold text-white">Suggested Wave Plan</span>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${suggestion.ai ? "border-emerald-500/40 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>
                  {suggestion.ai ? "AI" : "Heuristic"}
                </span>
              </div>
              <button onClick={() => setSuggestion(null)} data-testid="suggest-close" className="text-zinc-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              {!suggestion.ai && (
                <div className="text-[11px] text-zinc-500">AI planning is offline (Universal Key inactive) — showing a size/complexity-based plan you can apply now.</div>
              )}
              {suggestion.waves.map((w, i) => (
                <div key={i} className="rounded-md border border-zinc-800 bg-zinc-900 p-3">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-sm" style={{ background: WAVE_COLORS[i % WAVE_COLORS.length] }} />
                    <span className="text-sm font-bold text-white">{w.name}</span>
                  </div>
                  {w.rationale && <div className="text-[12px] text-zinc-400 mt-1">{w.rationale}</div>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {w.sites.map((s) => <span key={s.site_id} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">{s.name}</span>)}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={() => setSuggestion(null)} className="text-[12px] px-3.5 py-2 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</button>
              <button onClick={applySuggestion} disabled={applying} data-testid="suggest-apply-btn" className="text-[12px] font-semibold px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white">
                {applying ? "Applying…" : "Apply this plan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
